import cn from "classnames";
import { useTranslation } from "react-i18next";
import { Popover, PopoverContent, PopoverTrigger } from "~/atoms/popover";
import { Button } from "~/atoms/button";
import { CalendarIcon, Trash } from "lucide-react";
import { Calendar } from "~/atoms/calendar";
import { type Matcher } from "react-day-picker";
import { formatDate } from "~/helpers/date";
import dayjs from "dayjs";
import { useState } from "react";
import { useLanguageContext } from "~/context/language";

interface Props {
  value?: Date | null;
  disabled?: boolean;
  onChange?: (date?: Date) => void;
  minDate?: Date;
  maxDate?: Date;
  align?: "start" | "center" | "end";
}

const DatePicker = ({
  onChange,
  value,
  disabled,
  minDate,
  maxDate,
  align = "start",
}: Props) => {
  const { locale } = useLanguageContext();

  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleSelect = (date?: Date) => {
    onChange?.(date);
    if (date) setOpen(false);
  };

  const handleClear = () => {
    onChange?.(undefined);
  };

  const disabledRange: Matcher[] = [];
  if (minDate) disabledRange.push({ before: minDate });
  if (maxDate) disabledRange.push({ after: maxDate });

  const now = dayjs();
  const startMonth =
    minDate ?? now.subtract(10, "year").startOf("year").toDate();

  const requestedEndMonth =
    maxDate ??
    (minDate && now.isBefore(minDate) ? dayjs(minDate) : now)
      .add(10, "year")
      .endOf("year")
      .toDate();

  const endMonth =
    requestedEndMonth < startMonth
      ? dayjs(startMonth).endOf("month").toDate()
      : requestedEndMonth;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="field"
          disabled={disabled}
          className={cn("w-full text-left", !value && "text-muted-foreground")}
        >
          <div className="flex items-center gap-2">
            <CalendarIcon />
            {value ? (
              formatDate(value)
            ) : (
              <span>{t(($) => $.commons.customSelect.selectDate)}</span>
            )}
          </div>
          {value && !disabled && (
            <div
              className="hover:text-muted-foreground cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleClear();
              }}
            >
              <Trash className="size-3.5" />
            </div>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="h-74 w-auto p-0" align={align}>
        <Calendar
          mode="single"
          selected={value ?? undefined}
          onSelect={handleSelect}
          autoFocus
          disabled={disabledRange}
          defaultMonth={value ?? minDate ?? undefined}
          locale={locale}
          captionLayout="dropdown"
          startMonth={startMonth}
          endMonth={endMonth}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePicker;
